// src/pages/TestForm/index.js

import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Form,
  Input,
  InputNumber,
  Switch,
  DatePicker,
  Button,
  Card,
  Row,
  Col,
  Select,
  Table,
  Modal,
  Progress,
  Tooltip,
} from 'antd';
import { getChaptersByLevel, getExerciseDetailsById } from '../../services/exerciseService';
import { db } from '../../firebase-config';
import { collection, getDocs, query, where } from 'firebase/firestore';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

/* ===== スタイル ===== */
const twoLineClamp = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};
const linkishButton = {
  padding: 0,
  height: 'auto',
  textAlign: 'left',
  display: 'inline-block',
  width: '100%',
};
const nowrap = { whiteSpace: 'nowrap' };

/** parts からプレーンテキストを抽出（2行クランプ用） */
const partsToPlainText = (parts) => {
  if (!Array.isArray(parts)) return '';
  return parts.map((part) => part.text || part.value).join('');
};

/** ruby に対応した全文表示 */
const renderFullContent = (parts) => {
  if (!Array.isArray(parts)) {
    return <Paragraph>内容がありません。</Paragraph>;
  }
  return (
    <Paragraph style={{ fontSize: '16px', lineHeight: '1.8' }}>
      {parts.map((part, index) =>
        part.type === 'ruby' ? (
          <ruby key={index}>
            {part.text}
            <rt>{part.ruby}</rt>
          </ruby>
        ) : (
          <span key={index}>{part.value}</span>
        )
      )}
    </Paragraph>
  );
};

const TestForm = ({
  form,
  onFinish,
  isEditMode = false,
  saving = false,
  initialData = null,
}) => {
  const [isTimed, setIsTimed] = useState(initialData?.isTimed || false);
  const [isAvailabilitySet, setIsAvailabilitySet] = useState(
    !!(initialData?.startTime || initialData?.endTime)
  );
  const [selectedLevel, setSelectedLevel] = useState(initialData?.levelId || null);
  const [availableExercises, setAvailableExercises] = useState([]);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // 問題ごとの誤答統計: { [id]: { attempts, correct, wrong, wrongPercent } }
  const [globalStats, setGlobalStats] = useState({});

  const [isContentModalVisible, setIsContentModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalTitle, setModalTitle] = useState('');

  /** 編集時に初期レベルを同期 */
  useEffect(() => {
    if (isEditMode && initialData?.levelId) {
      setSelectedLevel(initialData.levelId);
    }
  }, [isEditMode, initialData]);

  /** レベルに応じて問題一覧を読み込み */
  useEffect(() => {
    if (!selectedLevel) {
      setAvailableExercises([]);
      setGlobalStats({});
      return;
    }

    const chapters = getChaptersByLevel(selectedLevel);
    const allExercisesInLevel = chapters.flatMap((chapter) =>
      chapter.exercises.map((ex) => {
        const details = getExerciseDetailsById(ex.id);
        return {
          ...ex,
          content: details?.questions?.[0]?.content,
        };
      })
    );

    setAvailableExercises(allExercisesInLevel);

    if (!isEditMode) {
      setSelectedExercises([]);
    }
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [selectedLevel, isEditMode]);

  /** 編集時：選択済みリストを復元 */
  useEffect(() => {
    if (isEditMode && initialData?.questions && availableExercises.length > 0) {
      const initialSelectedIds = new Set(initialData.questions.map((q) => q.exerciseId));
      const initialSelectedWithDetails = availableExercises
        .filter((ex) => initialSelectedIds.has(ex.id))
        .map((ex) => {
          const questionData = initialData.questions.find((q) => q.exerciseId === ex.id);
          return { ...ex, points: questionData.points };
        });
      setSelectedExercises(initialSelectedWithDetails);
    }
  }, [isEditMode, initialData, availableExercises]);

  /** 選択中レベルの各問題に対する全ユーザーの誤答率(%)を取得 */
  useEffect(() => {
    const fetchWrongRate = async () => {
      try {
        const ids = availableExercises.map((e) => e.id);
        if (ids.length === 0) {
          setGlobalStats({});
          return;
        }

        // Firestore の where 'in' は最大 10 件 → 分割して取得
        const chunks = [];
        for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));

        const progressRef = collection(db, 'progress');
        const agg = {}; // { id: { attempts, correct } }

        for (const c of chunks) {
          const qy = query(progressRef, where('exerciseId', 'in', c));
          const snap = await getDocs(qy);
          snap.docs.forEach((d) => {
            const data = d.data();
            const exId = data.exerciseId;
            if (!exId) return;

            const attempts = Number(data.attempts ?? 0);
            const correct = Number(data.completedCount ?? 0);

            if (!agg[exId]) agg[exId] = { attempts: 0, correct: 0 };
            agg[exId].attempts += attempts > 0 ? attempts : 0;
            agg[exId].correct += Math.min(correct, attempts);
          });
        }

        const result = {};
        Object.keys(agg).forEach((exId) => {
          const a = agg[exId].attempts;
          const c = agg[exId].correct;
          const w = Math.max(a - c, 0);
          const wrongPercent = a > 0 ? Math.round((w / a) * 100) : 0;
          result[exId] = { attempts: a, correct: c, wrong: w, wrongPercent };
        });

        setGlobalStats(result);
      } catch (err) {
        console.error('fetchWrongRate error:', err);
        setGlobalStats({});
      }
    };

    fetchWrongRate();
  }, [availableExercises]);

  /** 合計点 */
  const totalScore = useMemo(() => {
    return selectedExercises.reduce((total, ex) => total + (ex.points || 0), 0);
  }, [selectedExercises]);

  /** 送信用の隠しフィールドを書き込む */
  useEffect(() => {
    form.setFieldsValue({
      questions: selectedExercises.map(({ id, points }) => ({
        exerciseId: id,
        points: points || 0,
      })),
      totalScore: totalScore,
    });
  }, [selectedExercises, totalScore, form]);

  /** 左側（利用可能）テーブルの行選択 */
  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      // 既存の配点があれば維持。新規選択はデフォルト 10 点
      const updated = selectedRows.map((row) => {
        const existing = selectedExercises.find((ex) => ex.id === row.id);
        return existing || { ...row, points: 10 };
      });
      setSelectedExercises(updated);
    },
    selectedRowKeys: selectedExercises.map((ex) => ex.id),
  };

  const handlePointsChange = (exerciseId, points) => {
    setSelectedExercises((prev) =>
      prev.map((ex) => (ex.id === exerciseId ? { ...ex, points: points || 0 } : ex))
    );
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const showContentModal = (record) => {
    setModalTitle(`問題内容: ${record.title}`);
    setModalContent(record.content);
    setIsContentModalVisible(true);
  };

  const handleModalClose = () => {
    setIsContentModalVisible(false);
  };

  /** 左側テーブルの列定義 — 内容列を広く＋誤答(%)を表示 */
  const availableColumns = [
    {
      title: '問題ID',
      dataIndex: 'id',
      key: 'id',
      width: 90,
      align: 'center',
      render: (v) => <span style={nowrap}>{v}</span>,
    },
    {
      title: '問題名',
      dataIndex: 'title',
      key: 'title',
      width: 160,
      render: (v) => <span style={nowrap}>{v}</span>,
    },
    {
      title: '不正解率(%)',
      key: 'globalWrong',
      width: 120,
      align: 'center',
      sorter: (a, b) =>
        (globalStats[a.id]?.wrongPercent || 0) - (globalStats[b.id]?.wrongPercent || 0),
      render: (_, record) => {
        const st = globalStats[record.id] || { wrongPercent: 0, attempts: 0, wrong: 0 };
        const tip = `誤答 ${st.wrong}/${st.attempts} 回`;
        return (
          <Tooltip title={tip}>
            <div style={{ minWidth: 110 }}>
              <Progress percent={st.wrongPercent} size="small" />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: '内容', // 幅は指定せず残りを使用する
      dataIndex: 'content',
      key: 'content',
      render: (content, record) => {
        const fullText = partsToPlainText(content?.parts);
        return (
          <Button
            type="link"
            onClick={() => showContentModal(record)}
            style={linkishButton}
          >
            <span style={twoLineClamp} title={fullText}>
              {fullText}
            </span>
          </Button>
        );
      },
    },
  ];

  /** 右側（選択済み）テーブルの列定義 — コンパクトにして配点を読みやすく */
  const selectedColumns = [
    {
      title: '問題ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      align: 'center',
      render: (v) => <span style={nowrap}>{v}</span>,
    },
    {
      title: '問題名',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      render: (v) => <span style={nowrap}>{v}</span>,
    },
    {
      title: '配点',
      dataIndex: 'points',
      key: 'points',
      width: 120,
      align: 'center',
      render: (points, record) => (
        <InputNumber
          min={0}
          value={points}
          onChange={(value) => handlePointsChange(record.id, value)}
        />
      ),
    },
  ];

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Card title="基本情報">
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form.Item
              name="testName"
              label="テスト名"
              rules={[{ required: true, message: 'テスト名を入力してください。' }]}
            >
              <Input placeholder="例：期末試験 レベル1" disabled={isEditMode} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form.Item
              name="isTimed"
              label="時間制限あり"
              valuePropName="checked"
            >
              <Switch onChange={setIsTimed} />
            </Form.Item>
            {isTimed && (
              <Form.Item
                name="timeLimit"
                label="試験時間（分）"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            )}
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="isAvailabilitySet"
              label="有効期間を設定"
              valuePropName="checked"
            >
              <Switch onChange={setIsAvailabilitySet} />
            </Form.Item>
            {isAvailabilitySet && (
              <Form.Item
                name="availabilityRange"
                label="有効期間（開始〜終了）"
              >
                <RangePicker
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            )}
          </Col>
        </Row>
      </Card>

      <Card title="問題選択" style={{ marginTop: 16 }}>
        <Form.Item
          name="levelId"
          label="問題リストを読み込むレベルを選択"
          rules={[{ required: true, message: 'レベルを選択してください。' }]}
        >
          <Select
            placeholder="レベルを選択"
            onChange={(value) => setSelectedLevel(value)}
            disabled={isEditMode}
          >
            <Option value="level1">簿記3級</Option>
            <Option value="level2">簿記2級</Option>
            <Option value="level3">日商簿記</Option>
          </Select>
        </Form.Item>

        {selectedLevel && (
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <Title level={4}>利用可能な問題リスト</Title>
              <Table
                rowKey="id"
                rowSelection={rowSelection}
                columns={availableColumns}
                dataSource={availableExercises}
                bordered
                tableLayout="auto" // 内容列が残り幅を占有できるように
                pagination={{
                  ...pagination,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                }}
                onChange={handleTableChange}
              />
            </Col>

            <Col xs={24} lg={12}>
              <Title level={4}>選択した問題</Title>
              <Table
                rowKey="id"
                columns={selectedColumns}
                dataSource={selectedExercises}
                bordered
                tableLayout="auto"
                pagination={{ pageSize: pagination.pageSize }}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <Text strong>合計点</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong>{totalScore}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </Col>
          </Row>
        )}
      </Card>

      {/* 送信用の隠しフィールド */}
      <Form.Item name="questions" hidden />
      <Form.Item name="totalScore" hidden />

      <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
        <Button type="primary" htmlType="submit" loading={saving}>
          {isEditMode ? '更新する' : 'テストを保存'}
        </Button>
      </Form.Item>

      <Modal
        title={modalTitle}
        open={isContentModalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button key="close" onClick={handleModalClose}>
            閉じる
          </Button>,
        ]}
        width={800}
        centered
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '10px' }}>
          {modalContent ? (
            renderFullContent(modalContent.parts)
          ) : (
            <Text>内容がありません。</Text>
          )}
        </div>
      </Modal>
    </Form>
  );
};

export default TestForm;
